/*
 * /auth/massage-report — VAC massage billing report
 */
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	PageHeader,
	PageWithEffect,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { Download, Loader2 } from "lucide-react"
import { useState } from "react"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/massage-report")({
	component: MassageReportPage,
	loader: rolePageLoader(["massage_therapist", "admin", "super_admin"]),
})

function MassageReportPage() {
	const { data: practitioners = [] } = trpc.practitioner.getAllByFilter.useQuery({ where: { active: true } })

	const [selectedTherapists, setSelectedTherapists] = useState<string[]>([])
	const [startDate, setStartDate] = useState("")
	const [endDate, setEndDate] = useState("")
	const [hasRun, setHasRun] = useState(false)

	const [queryInput, setQueryInput] = useState<{
		therapistIds: string[]
		startDate: string
		endDate: string
	} | null>(null)

	const { data: rows = [], isFetching } = trpc.massageSession.generateBillingReport.useQuery(
		queryInput!,
		{ enabled: queryInput !== null },
	)

	function toggleTherapist(id: string) {
		setSelectedTherapists((prev) =>
			prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
		)
	}

	function handleGenerate() {
		if (!selectedTherapists.length || !startDate || !endDate) {
			toast.error("Select at least one therapist and a date range.")
			return
		}
		setQueryInput({ therapistIds: selectedTherapists, startDate, endDate })
		setHasRun(true)
	}

	function handleCsvDownload() {
		if (!rows.length) return
		const headers = ["Therapist", "Patient Name", "Date of Birth", "K-Number", "Session Date", "Start Time", "End Time", "Notes"]
		const csvRows = rows.map((r: any) => [
			csvCell(r.therapistName),
			csvCell(r.patientName),
			csvCell(r.patientDob ? new Date(r.patientDob).toLocaleDateString("en-CA") : ""),
			csvCell(r.kNumber ?? ""),
			csvCell(r.sessionDate ? new Date(r.sessionDate).toLocaleDateString("en-CA") : ""),
			csvCell(r.startTime ? new Date(r.startTime).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" }) : ""),
			csvCell(r.endTime ? new Date(r.endTime).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" }) : ""),
			csvCell(r.notes ?? ""),
		])
		const csv = [headers.join(","), ...csvRows.map((row) => row.join(","))].join("\n")
		const blob = new Blob([csv], { type: "text/csv" })
		const url = URL.createObjectURL(blob)
		const a = document.createElement("a")
		a.href = url
		a.download = `massage-billing-${startDate}-to-${endDate}.csv`
		a.click()
		URL.revokeObjectURL(url)
	}

	return (
		<PageWithEffect>
			<Container>
				<PageHeader title="Massage Billing Report" />

				<Card className="mb-6">
					<CardHeader>
						<CardTitle className="text-base">Report Filters</CardTitle>
						<CardDescription>Select therapists and date range for the VAC billing report.</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{/* Therapist multi-select */}
						<div>
							<p className="text-sm font-medium mb-2">Therapists</p>
							<div className="flex flex-wrap gap-2">
								{(practitioners as any[]).map((p) => {
									const isSelected = selectedTherapists.includes(p.id)
									return (
										<button
											key={p.id}
											onClick={() => toggleTherapist(p.id)}
											className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
												isSelected
													? "bg-primary text-primary-foreground border-primary"
													: "bg-background text-foreground border-border hover:border-primary"
											}`}
										>
											{p.firstName} {p.lastName}
										</button>
									)
								})}
								{!(practitioners as any[]).length && (
									<p className="text-sm text-muted-foreground">No practitioners found.</p>
								)}
							</div>
						</div>

						{/* Date range */}
						<div className="flex flex-wrap gap-4">
							<div>
								<label className="text-sm font-medium block mb-1">Start Date</label>
								<input
									type="date"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
									className="h-9 rounded-md border bg-background px-3 text-sm"
								/>
							</div>
							<div>
								<label className="text-sm font-medium block mb-1">End Date</label>
								<input
									type="date"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
									className="h-9 rounded-md border bg-background px-3 text-sm"
								/>
							</div>
						</div>

						<div className="flex gap-2 pt-1">
							<Button onClick={handleGenerate} disabled={isFetching}>
								{isFetching ? (
									<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
								) : (
									"Generate Report"
								)}
							</Button>
							{hasRun && rows.length > 0 && (
								<Button variant="outline" onClick={handleCsvDownload}>
									<Download className="w-4 h-4 mr-2" /> Download CSV
								</Button>
							)}
						</div>
					</CardContent>
				</Card>

				{/* Results */}
				{hasRun && !isFetching && (
					<Card>
						<CardHeader>
							<CardTitle className="text-base">
								{rows.length > 0
									? `${rows.length} Session${rows.length !== 1 ? "s" : ""} Found`
									: "No Sessions Found"}
							</CardTitle>
							{rows.length > 0 && (
								<CardDescription>
									{startDate} to {endDate}
								</CardDescription>
							)}
						</CardHeader>
						{rows.length > 0 && (
							<CardContent>
								<div className="overflow-x-auto">
									<table className="w-full text-sm">
										<thead>
											<tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
												<th className="text-left pb-2 pr-4 font-medium">Therapist</th>
												<th className="text-left pb-2 pr-4 font-medium">Patient</th>
												<th className="text-left pb-2 pr-4 font-medium">DOB</th>
												<th className="text-left pb-2 pr-4 font-medium">K-Number</th>
												<th className="text-left pb-2 pr-4 font-medium">Session Date</th>
												<th className="text-left pb-2 pr-4 font-medium">Time</th>
												<th className="text-left pb-2 font-medium">Notes</th>
											</tr>
										</thead>
										<tbody className="divide-y">
											{rows.map((r, i) => (
												<tr key={r.sessionId ?? i} className="py-2">
													<td className="py-2.5 pr-4 whitespace-nowrap">{r.therapistName || "—"}</td>
													<td className="py-2.5 pr-4 whitespace-nowrap font-medium">{r.patientName || "—"}</td>
													<td className="py-2.5 pr-4 whitespace-nowrap text-muted-foreground">
														{r.patientDob ? new Date(r.patientDob).toLocaleDateString("en-CA") : "—"}
													</td>
													<td className="py-2.5 pr-4 whitespace-nowrap text-muted-foreground">
														{r.kNumber ?? "—"}
													</td>
													<td className="py-2.5 pr-4 whitespace-nowrap">
														{r.sessionDate ? new Date(r.sessionDate).toLocaleDateString("en-CA", {
															year: "numeric", month: "short", day: "numeric",
														}) : "—"}
													</td>
													<td className="py-2.5 pr-4 whitespace-nowrap text-muted-foreground text-xs">
														{r.startTime && r.endTime
															? `${fmtT(r.startTime)} – ${fmtT(r.endTime)}`
															: r.startTime
																? fmtT(r.startTime)
																: "—"}
													</td>
													<td className="py-2.5 max-w-[280px]">
														<p className="truncate text-muted-foreground">{r.notes || "—"}</p>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</CardContent>
						)}
					</Card>
				)}
			</Container>
		</PageWithEffect>
	)
}

function fmtT(d: Date | string) {
	return new Date(d).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })
}

function csvCell(value: string): string {
	if (value.includes(",") || value.includes('"') || value.includes("\n")) {
		return `"${value.replace(/"/g, '""')}"`
	}
	return value
}
